/**
 * Native `<dialog>` + `showModal()`.
 *
 * The focus trap, the backdrop, and `Esc` come from the browser rather than
 * from hand-written key handling, and the opener regains focus on close. The
 * top layer also sidesteps the containing-block trap a hand-built backdrop
 * hits inside a `backdrop-filter` ancestor: `position: fixed` re-scopes to any
 * ancestor carrying `filter` / `transform` / `will-change` / `perspective`,
 * and a dialog rendered there lands in the wrong place with no error.
 *
 * `alert()` / `confirm()` / `prompt()` are banned repo-wide and lint-enforced.
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';

import { cn } from '../cn';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  /** Names the surface — becomes the accessible name via `aria-labelledby`. */
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  /**
   * Renders a close control in the head, labelled with this string. Omitted by
   * default: `Esc`, the backdrop and the footer's own cancel already reach
   * `onClose`, and a panel that is READ rather than answered is the case where
   * a head control earns its place.
   */
  closeLabel?: string;
  className?: string;
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  closeLabel,
  className,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Fires for Esc and for `close()` alike — one path out, always.
    const handleClose = (): void => onClose();

    // A click on the BACKDROP targets the dialog element itself; a click on
    // anything inside targets that child. Attached natively rather than as a
    // JSX prop because `<dialog>` is a non-interactive element to the a11y
    // linter, and the rule is right in general — the exception here is that
    // the listener exists to catch the backdrop, which has no element of its
    // own to bind to. `Esc` is the keyboard path and the browser provides it.
    const handleClick = (event: MouseEvent): void => {
      if (event.target === element) onClose();
    };

    element.addEventListener('close', handleClose);
    element.addEventListener('click', handleClick);
    return () => {
      element.removeEventListener('close', handleClose);
      element.removeEventListener('click', handleClick);
    };
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      className={cn(
        'w-full max-w-[32rem] rounded-lg border border-border bg-surface-raised p-5 text-ink shadow-elev-4',
        // The ::backdrop scrim is token-derived in styles/global.css.
        // Sheet below the desktop tier, centred dialog above it.
        'mb-0 mt-auto rounded-b-none lg:my-auto lg:rounded-b-lg',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <h2 id={titleId} className="min-w-0 flex-1 text-h3 font-bold">
          {title}
        </h2>
        {closeLabel ? (
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className="-me-1 -mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-sm text-ink-muted transition-colors duration-fast hover:bg-hover-bg hover:text-ink"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        ) : null}
      </div>
      {description ? <div className="mt-2 text-sm text-ink-muted">{description}</div> : null}
      {children ? <div className="mt-4">{children}</div> : null}
      {footer ? <div className="mt-5 flex flex-wrap gap-2">{footer}</div> : null}
    </dialog>
  );
}
